<template>
	<div>
		<slot name="searchFilter">
			<app-input
				v-if="searchable"
				v-model="computedSearch"
				name="search"
				placeholder="Search"
				:icon="searching ? 'spin-loading' : 'search'"
				class="pt-2"
				@update:modelValue="_onSearchInput"
			/>
		</slot>
		<table class="app-table" :class="$attrs.class ? $attrs.class : ''" v-bind="$attrs">
			<thead>
				<tr class="app-table-head-row">
					<th v-for="column in columns" :key="column.key" :class="column.class">
						<div v-if="!column.hidden" class="flex">
							<div class="flex gap-1" v-if="withSelectAll && column.key === 'selectAll'">
								<input
									id="selectAll"
									type="checkbox"
									v-model="selectAll"
									@change="$emit('selectAllRows', selectAll)"
								/>
								<label for="selectAll" class="flex flex-1 self-center">{{ column.label }}</label>
							</div>
							<p :class="`flex flex-1 ${chooseHeaderClass(column)}`">{{ $t(column.label) }}</p>
							<p class="flex flex-col" v-if="column.isSortable">
								<app-icon
									:class="
										sortByDirection === 'asc' && sortBy === column.key
											? 'text-primary'
											: 'text-secondary'
									"
									class="float-right self-center h-8px cursor-pointer"
									icon="sort-up"
									@click="fireSortBy(column.key, 'asc')"
								/>
								<app-icon
									:class="
										sortByDirection !== 'asc' && sortBy === column.key
											? 'text-primary'
											: 'text-secondary'
									"
									class="float-right self-center h-8px cursor-pointer"
									icon="sort-down"
									@click="fireSortBy(column.key, 'desc')"
								/>
							</p>
						</div>
					</th>
					<th class="actions" v-if="$slots.actions">{{ actionsColName }}</th>
				</tr>
			</thead>
			<tbody>
				<template v-for="(item, index) in tableData" :key="index">
					<tr
						class="app-table-body-row"
						:class="[
							{ 'bg-gray-300 hover:bg-gray-400': item[trackBy] && item[trackBy] === selectedItemValue },
							{ 'cursor-pointer': expandable },
						]"
						@click="onRowClick(item, index)"
					>
						<td
							v-for="column in columns"
							:class="[chooseClass(item, column)]"
							:key="`${index}-${column.key}`"
						>
							<div v-if="!column.hidden">
								<span v-if="!$slots[column.key]">{{ item[column.key] }}</span>
								<span v-if="$slots[column.key]">
									<slot :name="column.key" v-bind:item="item" v-bind:index="index" />
								</span>
							</div>
						</td>
						<td class="actions" v-if="$slots.actions">
							<slot name="actions" v-bind:item="item" v-bind:index="index" />
						</td>
					</tr>
					<transition name="slide-down" mode="out-in">
						<tr
							class="app-table-body-row no-hover"
							v-if="shouldShowDetailsRow[index] || openedIndex === index"
						>
							<td :colspan="columns.length">
								<slot name="appDetails" v-bind:item="item" v-bind:index="index" />
							</td>
						</tr>
					</transition>
				</template>
			</tbody>
		</table>
	</div>
</template>

<script>
	import AppIcon from '@/components/app/AppIcon';
	import UtilityService from '@/services/UtilityService';
	import { debounce } from 'debounce';

	export default {
		name: 'AppTable',
		emits: ['rowClick', 'sortBy', 'onSearch', 'selectAllRows'],
		props: {
			columns: {
				type: Array,
				required: true,
			},
			data: {
				type: Array,
				required: true,
			},
			loading: {
				type: Boolean,
				required: false,
			},
			selectedItemValue: {
				type: Number,
				required: false,
			},
			trackBy: {
				type: String,
				default: '',
			},
			actionsColName: {
				type: String,
				default: 'Actions',
			},
			searchable: {
				required: false,
				default: true,
			},
			remoteSearchable: {
				required: false,
				default: false,
			},
			searching: {
				required: false,
				default: false,
			},
			expandable: {
				required: false,
				default: false,
			},
			oneAtATime: {
				required: false,
				default: false,
			},
			withSelectAll: {
				type: Boolean,
				default: false,
			},
		},
		components: {
			AppIcon,
		},
		computed: {
			computedSearch: {
				get() {
					return this.search;
				},
				set: debounce(function(newValue) {
					this.search = newValue;
				}, 100),
			},
			tableData() {
				const column = this.columns.find((c) => {
					return c.key === this.sortBy;
				});
				if (!column && this.search && !this.remoteSearchable) {
					return [...this.data].filter((item) =>
						UtilityService.dynamicFilter(item, this.columns, this.computedSearch)
					);
				}
				return [...this.data]
					.filter((item) =>
						UtilityService.dynamicFilter(
							item,
							this.columns,
							this.remoteSearchable ? '' : this.computedSearch
						)
					)
					.sort((a, b) => {
						return UtilityService.dynamicSortBy(
							a,
							b,
							column && column.keyProp ? column.keyProp : this.sortBy,
							this.sortByDirection
						);
					});
			},
		},
		data: () => ({
			sortBy: '',
			sortByDirection: '',
			search: '',
			shouldShowDetailsRow: {},
			openedIndex: -1,
			selectAll: false,
		}),
		methods: {
			_onSearchInput: debounce(function(v) {
				this.$emit('onSearch', v);
			}, 300),
			fireSortBy(prop, direction) {
				this.sortBy = prop;
				this.sortByDirection = direction;
				this.$emit('sortBy', { prop, direction });
			},
			onRowClick(item, index) {
				if (this.expandable) {
					if (this.oneAtATime) {
						if (this.openedIndex === index) {
							this.openedIndex = -1;
						} else {
							this.openedIndex = index;
						}
					} else {
						this.shouldShowDetailsRow[index] = !this.shouldShowDetailsRow[index];
					}
				}
				this.$emit('rowClick', item);
			},
			chooseHeaderClass(column) {
				if (
					[
						'Employees No',
						'Daily Budget',
						'Company Budget',
						'Employee Budget',
						'Total Budget',
						'Company Daily Budget',
						'Employee Daily Budget',
						'Budget',
						'Days No',
						'Amount',
						'Progressive',
						'Discount',
						'avgValue',
						'totalValue',
						'totalQuantity',
						'growthPercentage',
						'Goods Total',
						'Orders No',
						'AVG Basket Value',
						'AVG Food Rating',
						'Food Rates No',
						'AVG Delivery Rating',
						'Delivery Rates No',
						'Rejected Orders',
						'AVG Basket Items',
						'AVG Delivery Rate',
						'AVG Delivery Distance',
						'Quantity',
						'Price',
						'VAT',
						'Amount with VAT',
						'invoice',
					].includes(column.label)
				) {
					return 'text-right justify-end';
				}
			},
			chooseClass(item, column) {
				if (
					(typeof item[column.key] === 'number' &&
						![
							'Id',
							'ID',
							'Type',
							'Priority',
							'Tot Users',
							'Channel',
							'GoalType',
							'Status',
							'Doc Type',
						].includes(column.label)) ||
					['avgValue', 'growthPercentage'].includes(column.label)
				) {
					return 'text-right';
				} else {
					return 'text-left';
				}
			},
		},
		mounted() {
			let defaultSortIndex = this.columns.findIndex((c) => c.initialSort);
			if (defaultSortIndex === -1) {
				return;
			}
			this.sortBy = this.columns[defaultSortIndex].key;
			this.sortByDirection = this.columns[defaultSortIndex].initialSortDirection
				? this.columns[defaultSortIndex].initialSortDirection
				: 'desc';
		},
	};
</script>
